import { MenuList } from 'Components/Commons/Sidebar/data';
import { URL_FORGOT_PASSWORD, URL_LOGIN, URL_RESET_PASSWORD } from 'Helpers/Paths';
import { useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';

/**
 * @hook
 * @description A custom hook that handles route protection and redirection based on user permissions.
 * This hook watches for route changes and checks if the current user has permission to access
 * the requested page. If the user lacks the required permissions, they are redirected to a 404 page.
 *
 * The hook automatically excludes public pages (login, forgot password, reset password) from permission checks.
 * For protected routes, it matches the current path against the menu list and checks the user's
 * permissions for the corresponding module.
 *
 * @returns {void} This hook doesn't return anything but performs navigation side effects
 *
 * @example
 * // In a component or layout wrapper
 * function ProtectedLayout() {
 *   useWatchRedirection();
 *   return <Outlet />;
 * }
 *
 * @throws {NavigationError} Redirects to 404 page if user lacks required permissions
 *
 * @see MenuList - Available menu items and their permissions
 * @see URL_LOGIN - Public route constants
 */
const useWatchRedirection = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const adminData = useSelector((state) => state.Auth.adminData);
    const publicPages = [URL_LOGIN, URL_FORGOT_PASSWORD, URL_RESET_PASSWORD];
    if (!publicPages.includes(location.pathname)) {
        const getModuleNameByLocation = MenuList.find((item) =>
            item.submenu
                ? item.submenu.find((subItem) => subItem.path === location.pathname)
                : item.path === location.pathname
        );

        const checkPageVisibleOrNot = (adminData?.permissions || []).find(
            (perm) => perm.moduleName === getModuleNameByLocation?.permissionName
        );

        if (checkPageVisibleOrNot && !checkPageVisibleOrNot?.canView) {
            navigate('*', { replace: true });
        }
    }
};

export default useWatchRedirection;
